import React from 'react'
import PropTypes from 'prop-types'
import {Table} from 'antd'
import {DASH, formatInt} from '../../helpers/format'
import {URGENCY_GROUP_COLORS} from '../../constants'

/**
 * Bảng Phân bổ theo mức khẩn — FR-M17-20 bước 3, BR-N3-22, BR-N3-23.
 *
 * Mỗi dòng là một ô mức khẩn, tức giao của một SKU với một cột số ngày.
 *
 * ⚠️ KHÔNG lọc bỏ ô có tỷ lệ 0% (BR-N3-23). Ô 0% mà có item bị kéo vào là chỗ DUY NHẤT người dùng
 *    lần ra được phần chênh đến từ đâu; ẩn đi thì dòng tóm tắt nói "kéo thêm n item" mà không có
 *    cách nào truy ra n item đó nằm ở đâu.
 */
const labelOf = (layout, groupKey, columnKey) => {
    const groupIndex = (layout || []).findIndex((each) => each.key === groupKey)
    const group = groupIndex < 0 ? null : layout[groupIndex]
    const columnIndex = group ? group.columns.findIndex((each) => each.key === columnKey) : -1
    const column = columnIndex < 0 ? null : group.columns[columnIndex]

    return {
        group: group ? group.label : groupKey,
        column: column ? column.label : columnKey,
        /*
         * Thứ hạng để SẮP XẾP, tách khỏi nhãn để hiển thị.
         *
         * ⚠️ Sắp theo nhãn là sai, và sai theo kiểu khó thấy: cột số ngày có cả `"trên 6 ngày"` lẫn
         *    `"1.5 ngày"`, nên so chuỗi sẽ xếp `"trên 6 ngày"` trước `"1 ngày"`, còn so số thì
         *    `"1.5"` rơi đâu đó giữa `"1"` và `"2"` chỉ vì may mắn. `layout` đã khai sẵn đúng trình
         *    tự mức khẩn của từng nhóm — dùng chỉ số trong đó là bám đúng nguồn duy nhất.
         *
         * Nhóm cũng vậy: "Ngày sản xuất còn lại" < "Quá hạn (do note)" < "Quá hạn" là thứ tự mức
         * khẩn, không phải thứ tự bảng chữ cái.
         */
        groupRank: groupIndex < 0 ? Number.MAX_SAFE_INTEGER : groupIndex,
        columnRank: columnIndex < 0 ? Number.MAX_SAFE_INTEGER : columnIndex,
        groupColor: group ? group.color : null,
    }
}

/** So chuỗi có dấu tiếng Việt cho đúng, và ổn định khi hai giá trị bằng nhau. */
const byText = (key) => (left, right) => String(left[key] || '').localeCompare(String(right[key] || ''), 'vi')

const byNumber = (key) => (left, right) => (left[key] || 0) - (right[key] || 0)

function AllocationTab({cells, layout}) {
    const rows = cells.map((cell) => {
        const labels = labelOf(layout, cell.groupKey, cell.columnKey)

        return {...cell, key: `${cell.groupKey}.${cell.columnKey}|${cell.skuId}`, ...labels}
    })

    /*
     * Mọi cột đều sắp được. Bảng này có tới vài trăm dòng và người dùng mở nó ra để trả lời những
     * câu rất cụ thể — "ô nào bị kéo thêm nhiều nhất", "SKU nào vượt mục tiêu xa nhất" — mà không
     * sắp được thì phải cuộn tay qua từng trang.
     *
     * Mặc định sắp theo CỘT SỐ NGÀY (xem cột đó). Bấm lần thứ ba vào một tiêu đề là antd trả bảng về
     * thứ tự service trả — thứ tự đó cũng có nghĩa riêng nên vẫn giữ đường về.
     */
    const columns = [
        {title: 'SKU', dataIndex: 'skuId', sorter: byText('skuId')},
        {
            title: 'Nhóm mức khẩn',
            dataIndex: 'group',
            sorter: byNumber('groupRank'),
            /*
             * Chấm màu lấy CÙNG một bảng màu với tiêu đề nhóm ở bảng tỷ lệ (`URGENCY_GROUP_COLORS`),
             * không tự đặt màu mới — hai sắc xanh lệch nhau còn khó chịu hơn không tô.
             *
             * ⚠️ Màu chỉ là kênh PHỤ (AC-UI-01): tên nhóm vẫn in đầy đủ ngay cạnh, nên người không
             *    phân biệt được màu vẫn đọc được đủ thông tin.
             */
            render: (value, row) => (
                <span className="VcUrgencyDotLabel">
                    <i
                        className="VcUrgencyDot"
                        style={{background: URGENCY_GROUP_COLORS[row.groupColor] || 'transparent'}}
                    />
                    {value}
                </span>
            ),
        },
        {
            title: 'Cột số ngày',
            dataIndex: 'column',
            // Trong cùng một nhóm mới so được cột với nhau: cột "1 ngày" của nhóm xanh và của nhóm đỏ
            // là hai mức khẩn khác hẳn nhau.
            sorter: (left, right) => left.groupRank - right.groupRank || left.columnRank - right.columnRank,
            /*
             * Sắp xếp MẶC ĐỊNH của cả bảng.
             *
             * Đặt ở cột này chứ không ở "Nhóm mức khẩn" vì sorter của nó đã gồm luôn thứ hạng nhóm:
             * cùng gom theo nhóm như cột kia, nhưng TRONG mỗi nhóm còn xếp đúng trình tự mức khẩn
             * thay vì để nguyên thứ tự dữ liệu. Một phép sắp, hai tầng thông tin.
             *
             * Giảm dần: nhóm khẩn nhất lên đầu (đỏ → vàng → xanh), và trong mỗi nhóm thì quá hạn lâu
             * nhất lên trước. Bảng này để trả lời "đang định làm gì trước", nên thứ cần xử lý gấp
             * phải nằm ở màn hình đầu tiên thay vì cuối danh sách vài trăm dòng.
             *
             * ⚠️ Vì thế nó đọc NGƯỢC chiều với bảng tỷ lệ phía trên (bày xanh → vàng → đỏ theo
             *    `urgencyLayout`). Cố ý: hai bảng trả lời hai câu hỏi khác nhau — bảng trên là bảng
             *    NHẬP liệu nên bám thứ tự khai báo, bảng này là bảng ĐỌC nên bám mức ưu tiên xử lý.
             *    Chấm màu ở cột "Nhóm mức khẩn" là thứ nối hai bảng lại với nhau.
             *
             *    Đây là ĐỔI thứ tự mặc định của một tab đang chạy, nên đã đưa PO chốt (vòng review 4,
             *    finding [82]) — PO xác nhận cần đổi. Đừng gỡ `defaultSortOrder` để "cho khớp bảng
             *    trên": ràng buộc SRS-002 §5 của vùng nhập tỷ lệ là đối chiếu với MÀN HÌNH NGUỒN
             *    bên ngoài, không phải với bảng này.
             */
            defaultSortOrder: 'descend',
        },
        {title: 'Số lượng gốc', dataIndex: 'qty', align: 'right', render: formatInt, sorter: byNumber('qty')},
        {
            title: 'Tỷ lệ',
            dataIndex: 'ratio',
            align: 'right',
            render: (value) => `${value}%`,
            sorter: byNumber('ratio'),
        },
        {
            title: 'Số lượng mục tiêu',
            dataIndex: 'target',
            align: 'right',
            render: formatInt,
            sorter: byNumber('target'),
        },
        {
            title: 'Kéo thêm do trọn đơn',
            dataIndex: 'pulled',
            align: 'right',
            // Tách hẳn khỏi cột số lượng thực tế (BR-N3-22) — gộp vào thì không lần ra phần vượt.
            render: (value) => (value ? <span className="VcAllocPulled">{formatInt(value)}</span> : DASH),
            sorter: byNumber('pulled'),
        },
        {
            title: 'Số lượng thực tế',
            dataIndex: 'actual',
            align: 'right',
            render: (value) => <b>{formatInt(value)}</b>,
            sorter: byNumber('actual'),
        },
    ]

    return (
        <div className="VcAllocTab">
            <div className="VcHint">
                Mỗi dòng là một ô mức khẩn, tức giao của một SKU với một cột số ngày. Cột{' '}
                <b>Kéo thêm do trọn đơn</b> là số item không nằm trong quota nhưng vẫn được lấy vì nằm
                chung đơn với item đã chọn. Ô đặt tỷ lệ <b>0%</b> mà vẫn có item bị kéo vào thì vẫn hiện
                thành dòng, với số lượng mục tiêu bằng 0 — đây là cách lần ra phần chênh lệch đến từ đâu.
            </div>
            <Table
                size="small"
                rowKey="key"
                columns={columns}
                dataSource={rows}
                showSorterTooltip={false}
                pagination={{pageSize: 50, showSizeChanger: false, hideOnSinglePage: true}}
            />
        </div>
    )
}

AllocationTab.propTypes = {
    cells: PropTypes.arrayOf(PropTypes.object).isRequired,
    layout: PropTypes.arrayOf(PropTypes.object),
}

AllocationTab.defaultProps = {layout: []}

export default AllocationTab
